import { noop } from 'lodash';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { SIDEBAR_COLLAPSED } from '~/constants/localStorageKeys';
import { isDevOrTestStage } from '~/swr-hooks/utils';

type SideNavContextValue = {
  isSideNavOpen: boolean;
  toggleSideNav: () => void;
  openSideNav: () => void;
  closeSideNav: () => void;
};

const defaultValue: SideNavContextValue = {
  isSideNavOpen: false,
  toggleSideNav: noop,
  openSideNav: noop,
  closeSideNav: noop,
};

const SideNavProviderContext = createContext(defaultValue);

export const SideNavProvider = ({ children }: { children?: ReactNode | undefined }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const localStorageValue = window.localStorage.getItem(SIDEBAR_COLLAPSED);

    setIsCollapsed(localStorageValue === 'true');
  }, []);

  const handleToggle = useCallback(() => {
    setIsCollapsed((isCollapsed) => {
      window.localStorage.setItem(SIDEBAR_COLLAPSED, String(!isCollapsed));

      return !isCollapsed;
    });
  }, []);

  const handleOpen = useCallback(() => {
    setIsCollapsed(false);
    window.localStorage.setItem(SIDEBAR_COLLAPSED, 'false');
  }, []);

  const handleClose = useCallback(() => {
    setIsCollapsed(true);
    window.localStorage.setItem(SIDEBAR_COLLAPSED, 'true');
  }, []);

  const value = useMemo(
    () => ({
      isSideNavOpen: !isCollapsed,
      toggleSideNav: handleToggle,
      openSideNav: handleOpen,
      closeSideNav: handleClose,
    }),
    [handleToggle, isCollapsed, handleClose, handleOpen],
  );

  return <SideNavProviderContext.Provider value={value}>{children}</SideNavProviderContext.Provider>;
};

export const useSideNavContext = () => {
  const context = useContext(SideNavProviderContext);

  if (context === defaultValue) {
    const error = 'useSideNavContext used outside of SideNavProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
